import { useState, useEffect, useCallback, useRef } from "react";
import "./ForecastTimeline.css";

const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
const MIN_OFFSET = -24; // 24h in the past
const MAX_OFFSET = 36;  // 36h in the future
const RANGE = MAX_OFFSET - MIN_OFFSET;

const ForecastTimeline = ({
  forecastOffset,
  setForecastOffset,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [timezoneLabel, setTimezoneLabel] = useState("CET");
  const [localOffset, setLocalOffset] = useState(forecastOffset);
  const [isDragging, setIsDragging] = useState(false);

  const playIntervalRef = useRef(null);
  const offsetRef = useRef(forecastOffset);
  const debounceRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => { offsetRef.current = forecastOffset; }, [forecastOffset]);

  // Keep localOffset in sync when changed externally (arrows, play button)
  useEffect(() => {
    if (!isDragging) {
      setLocalOffset(forecastOffset);
    }
  }, [forecastOffset, isDragging]);

  const detectFranceTimezone = useCallback(() => {
    const now = new Date();
    const franceTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Paris" }));
    const utcTime = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
    const diff = (franceTime - utcTime) / (1000 * 60 * 60);
    return diff >= 2 ? "CEST" : "CET";
  }, []);

  useEffect(() => {
    setTimezoneLabel(detectFranceTimezone());
    const interval = setInterval(() => setTimezoneLabel(detectFranceTimezone()), 3600000);
    return () => clearInterval(interval);
  }, [detectFranceTimezone]);

  const getFranceTime = useCallback(() => {
    return new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" }));
  }, []);

  // Use localOffset for display so the pill updates live while dragging
  const displayTime = (() => {
    const t = getFranceTime();
    t.setHours(t.getHours() + localOffset);
    return t;
  })();

  const formatDate = (date) => `${date.getDate()} ${MONTHS[date.getMonth()]}`;
  const formatHour = (date) => `${date.getHours().toString().padStart(2, "0")}:00`;

  // ── Milestone markers (day boundaries + noon), now spanning the past too ──
  const buildMilestones = useCallback(() => {
    const baseHour = getFranceTime().getHours();
    const markers = [];
    for (let h = MIN_OFFSET; h <= MAX_OFFSET; h++) {
      if (h === 0) continue; // skip "now", it's implied by the thumb position
      const absH = baseHour + h;
      const hourOfDay = ((absH % 24) + 24) % 24; // safe modulo for negative hours
      const isMidnight = hourOfDay === 0;
      const isNoon = hourOfDay === 12;
      if (!isMidnight && !isNoon) continue;

      const t = new Date(getFranceTime());
      t.setHours(t.getHours() + h);
      const pct = ((h - MIN_OFFSET) / RANGE) * 100;

      markers.push({
        pct,
        major: isMidnight,
        label: isMidnight ? `${t.getDate()} ${MONTHS[t.getMonth()]}` : "12:00",
      });
    }
    return markers;
  }, [getFranceTime]);

  const milestones = buildMilestones();

  // Position (in %) of the "now" marker on the track, useful for a visual reference point
  const nowPct = ((0 - MIN_OFFSET) / RANGE) * 100;

  // ── Drag logic ──
  const offsetFromPointer = useCallback((e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    return Math.round(MIN_OFFSET + ratio * RANGE);
  }, []);

  const handlePointerDown = useCallback((e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    const offset = offsetFromPointer(e);
    setLocalOffset(offset);
    // Clear any pending debounce
    clearTimeout(debounceRef.current);
  }, [offsetFromPointer]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    const offset = offsetFromPointer(e);
    setLocalOffset(offset);
    // Reset debounce — only fire tile request after 400ms of no movement
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setForecastOffset(offset);
    }, 400);
  }, [isDragging, offsetFromPointer, setForecastOffset]);

  const handlePointerUp = useCallback((e) => {
    if (!isDragging) return;
    setIsDragging(false);
    clearTimeout(debounceRef.current);
    const offset = offsetFromPointer(e);
    setLocalOffset(offset);
    setForecastOffset(offset); // Fire immediately on release
  }, [isDragging, offsetFromPointer, setForecastOffset]);

  // Cleanup debounce on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // ── Arrow controls (now clamp to MIN_OFFSET / MAX_OFFSET instead of 0 / MAX) ──
  const incrementHour = useCallback(() => setForecastOffset((p) => Math.min(p + 1, MAX_OFFSET)), [setForecastOffset]);
  const decrementHour = useCallback(() => setForecastOffset((p) => Math.max(p - 1, MIN_OFFSET)), [setForecastOffset]);
  const incrementDate = useCallback(() => setForecastOffset((p) => Math.min(p + 24, MAX_OFFSET)), [setForecastOffset]);
  const decrementDate = useCallback(() => setForecastOffset((p) => Math.max(p - 24, MIN_OFFSET)), [setForecastOffset]);

  const skipToNextDay = useCallback(() => {
    const hoursToNextDay = 24 - getFranceTime().getHours();
    setForecastOffset((p) => Math.min(p + hoursToNextDay, MAX_OFFSET));
  }, [getFranceTime, setForecastOffset]);

  // Symmetric "jump to start of current day in the past" control
  const skipToPreviousDay = useCallback(() => {
    const hoursToPreviousDay = getFranceTime().getHours();
    setForecastOffset((p) => Math.max(p - (hoursToPreviousDay === 0 ? 24 : hoursToPreviousDay), MIN_OFFSET));
  }, [getFranceTime, setForecastOffset]);

  const togglePlay = useCallback(() => setIsPlaying((p) => !p), []);

  useEffect(() => {
    if (isPlaying) {
      playIntervalRef.current = setInterval(() => {
        const current = offsetRef.current;
        if (current >= MAX_OFFSET) {
          setIsPlaying(false);
          setForecastOffset(0);
          return;
        }
        setForecastOffset(current + 1);
      }, 2000);
    } else {
      clearInterval(playIntervalRef.current);
      playIntervalRef.current = null;
    }
    return () => { clearInterval(playIntervalRef.current); playIntervalRef.current = null; };
  }, [isPlaying, setForecastOffset]);

  const isAtMax = localOffset >= MAX_OFFSET;
  const isAtMin = localOffset <= MIN_OFFSET;

  return (
    <div className="forecast-timeline-container">
      <div className="forecast-timeline-pill">
        <button
          className={`timeline-play-btn ${isPlaying ? "playing" : ""}`}
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="timeline-divider" />

        <div className="timeline-section">
          <button className="timeline-arrow up" onClick={incrementDate} disabled={isAtMax} aria-label="Next day">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" /></svg>
          </button>
          <span className="timeline-value date">{formatDate(displayTime)}</span>
          <button className="timeline-arrow down" onClick={decrementDate} disabled={isAtMin} aria-label="Previous day">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" /></svg>
          </button>
        </div>

        <span className="timeline-separator">:</span>

        <div className="timeline-section hour-section">
          <button className="timeline-arrow up" onClick={incrementHour} disabled={isAtMax} aria-label="Next hour">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" /></svg>
          </button>
          <div className="timeline-hour-wrapper">
            <span className="timeline-value hour">{formatHour(displayTime)}</span>
            <span className="timeline-timezone">{timezoneLabel}</span>
          </div>
          <button className="timeline-arrow down" onClick={decrementHour} disabled={isAtMin} aria-label="Previous hour">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" /></svg>
          </button>
        </div>

        <div className="timeline-divider" />

        <button className="timeline-skip-btn" onClick={skipToPreviousDay} disabled={isAtMin} aria-label="Skip to previous day">
          <svg viewBox="0 0 24 24" fill="currentColor" style={{ transform: "scaleX(-1)" }}><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" /></svg>
        </button>
        <button className="timeline-skip-btn" onClick={skipToNextDay} disabled={isAtMax} aria-label="Skip to next day">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" /></svg>
        </button>
      </div>

      {/* ── Progress track with milestones ── */}
      <div className="timeline-progress-wrapper">
        {/* Milestone row */}
        <div className="timeline-milestone-row">
          {milestones.map(({ pct, major, label }) => (
            <div
              key={pct}
              className={`timeline-milestone ${major ? "major" : "minor"}`}
              style={{ left: `${pct}%` }}
            >
              <div className="timeline-milestone-tick" />
              <span className="timeline-milestone-label">{label}</span>
            </div>
          ))}
          {/* "Now" reference marker */}
          <div className="timeline-milestone now" style={{ left: `${nowPct}%` }}>
            <div className="timeline-milestone-tick now" />
            <span className="timeline-milestone-label">Maintenant</span>
          </div>
        </div>

        {/* Draggable progress track — fill now runs from the "now" mark, not from 0 */}
        <div
          ref={trackRef}
          className={`timeline-progress-track${isDragging ? " dragging" : ""}`}
          role="slider"
          aria-label="Forecast time"
          aria-valuemin={MIN_OFFSET}
          aria-valuemax={MAX_OFFSET}
          aria-valuenow={localOffset}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <div
            className="timeline-progress-fill"
            style={{
              left: `${Math.min(nowPct, ((localOffset - MIN_OFFSET) / RANGE) * 100)}%`,
              width: `${Math.abs(((localOffset - MIN_OFFSET) / RANGE) * 100 - nowPct)}%`,
            }}
          />
          <div
            className={`timeline-progress-thumb${isDragging ? " active" : ""}`}
            style={{ left: `${((localOffset - MIN_OFFSET) / RANGE) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ForecastTimeline;